import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getProjects } from '../api';

/* ═══════════════════════════════════════════════════════════════════════════
   AppHeader — redesigned shared top bar (warm-cream + sage)
   Renders on top-level routes only (hidden on project-detail, where
   ProjectHeader takes over). Left: page identity. Center/right: global
   project search (lazy client-side typeahead over getProjects). Far right:
   optional page action + notifications bell. Token-only colors.
   ═══════════════════════════════════════════════════════════════════════════ */

const FOCUS_RING = '0 0 0 3px var(--accent-ring)';

const STATUS_TONE = {
  Active: 'var(--success)',
  Planning: 'var(--accent)',
  'Pre-Construction': 'var(--accent)',
  'On Hold': 'var(--warning)',
  Complete: 'var(--text-3)',
  Closed: 'var(--text-3)',
};
const STATUS_SOFT = {
  Active: 'var(--success-soft)',
  Planning: 'var(--accent-soft)',
  'Pre-Construction': 'var(--accent-soft)',
  'On Hold': 'var(--warning-soft)',
  Complete: 'var(--bg-surface-2)',
  Closed: 'var(--bg-surface-2)',
};
const STATUS_LABEL = { 'Pre-Construction': 'Pre-Con' };

/* ─────────────── Inline SVG icons ─────────────── */

const Icon = ({ name, size = 16, sw = 1.6 }) => {
  const s = size;
  const p = (d) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      {d}
    </svg>
  );
  switch (name) {
    case 'search':
      return p(
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </>
      );
    case 'plus':
      return p(<path d="M12 5v14M5 12h14" />);
    case 'bell':
      return p(
        <>
          <path d="M6 8a6 6 0 0 1 12 0c0 4 1.5 5 2 6H4c.5-1 2-2 2-6Z" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </>
      );
    case 'x':
      return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'enter':
      return p(
        <>
          <path d="M9 10 4 15l5 5" />
          <path d="M20 4v7a4 4 0 0 1-4 4H4" />
        </>
      );
    case 'chevron-r':
      return p(<path d="m9 6 6 6-6 6" />);
    case 'building':
      return p(
        <>
          <path d="M3 21h18" />
          <path d="M6 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16" />
          <path d="M14 21V9h4a1 1 0 0 1 1 1v11" />
          <path d="M9 8h2M9 12h2M9 16h2" />
        </>
      );
    default:
      return null;
  }
};

/* ─────────────── Status pill ─────────────── */

const StatusPill = ({ status }) => {
  if (!status) return null;
  const tone = STATUS_TONE[status] || 'var(--text-3)';
  const soft = STATUS_SOFT[status] || 'var(--bg-surface-2)';
  const neutral = status === 'Complete' || status === 'Closed';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        flex: '0 0 auto',
        padding: '1px 7px 1px 6px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        color: neutral ? 'var(--text-3)' : tone,
        background: soft,
        border: `1px solid ${neutral ? 'var(--border-subtle)' : `color-mix(in oklch, ${tone} 20%, transparent)`}`,
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: 99, background: tone, flex: '0 0 auto' }} />
      {STATUS_LABEL[status] || status}
    </span>
  );
};

/* ─────────────── Result row ─────────────── */

const ResultRow = ({ project, active, onPick }) => (
  <button
    type="button"
    role="option"
    aria-selected={active}
    onMouseDown={(e) => {
      e.preventDefault();
      onPick(project);
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 11,
      width: '100%',
      textAlign: 'left',
      padding: '8px 10px',
      borderRadius: 8,
      cursor: 'pointer',
      border: 'none',
      background: active ? 'var(--bg-hover)' : 'transparent',
      transition: 'background 90ms ease',
    }}
  >
    <span
      style={{
        width: 30,
        height: 30,
        borderRadius: 8,
        flex: '0 0 auto',
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border-subtle)',
        color: 'var(--text-3)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon name="building" size={15} />
    </span>
    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, gap: 1 }}>
      <span
        style={{
          fontSize: 15,
          fontWeight: 500,
          color: 'var(--text-1)',
          letterSpacing: '-0.006em',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {project.project_name}
      </span>
      <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>
        {project.project_number || '—'}
      </span>
    </span>
    <StatusPill status={project.status} />
    {active && (
      <span style={{ flex: '0 0 auto', color: 'var(--text-4)', display: 'inline-flex', alignItems: 'center', marginLeft: 2 }}>
        <Icon name="enter" size={13} sw={1.7} />
      </span>
    )}
  </button>
);

/* ─────────────── Dropdown ─────────────── */

const labelStyle = {
  padding: '2px 8px 6px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: 'var(--text-4)',
};

const SearchDropdown = ({ state, query, rows, results, active, onPick }) => {
  const CAP = 8;
  const shown = rows.slice(0, CAP);
  const extra = (state === 'results' ? results.length : rows.length) - shown.length;

  return (
    <div
      role="listbox"
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        left: 0,
        right: 0,
        zIndex: 40,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-md)',
        overflow: 'hidden',
        padding: 6,
      }}
    >
      {state === 'loading' && (
        <>
          <div style={labelStyle}>Searching…</div>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 10px' }}>
              <span style={{ width: 30, height: 30, borderRadius: 8, flex: '0 0 auto', background: 'var(--bg-surface-2)' }} />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                <span style={{ height: 9, width: `${60 - i * 8}%`, borderRadius: 4, background: 'var(--bg-surface-2)' }} />
                <span style={{ height: 8, width: '28%', borderRadius: 4, background: 'var(--bg-surface-2)' }} />
              </span>
            </div>
          ))}
        </>
      )}

      {state === 'recent' && (
        <>
          <div style={labelStyle}>Projects</div>
          {shown.map((p, i) => (
            <ResultRow key={p.id} project={p} active={i === active} onPick={onPick} />
          ))}
          <div
            style={{
              borderTop: '1px solid var(--border-subtle)',
              marginTop: 6,
              paddingTop: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '8px 8px 4px',
              color: 'var(--text-4)',
              fontSize: 13,
            }}
          >
            <Icon name="search" size={13} /> Type to search all projects by name or number
          </div>
        </>
      )}

      {state === 'results' && (
        <>
          <div style={labelStyle}>Projects · {results.length}</div>
          {shown.map((p, i) => (
            <ResultRow key={p.id} project={p} active={i === active} onPick={onPick} />
          ))}
          {extra > 0 && (
            <div
              style={{
                padding: '9px 10px 5px',
                fontSize: 13,
                color: 'var(--text-3)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                borderTop: '1px solid var(--border-subtle)',
                marginTop: 4,
              }}
            >
              <span className="mono" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                +{extra}
              </span>{' '}
              more — keep typing to narrow
            </div>
          )}
        </>
      )}

      {state === 'nomatch' && (
        <div style={{ padding: '22px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6 }}>
          <span
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'var(--bg-surface-2)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-4)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 4,
            }}
          >
            <Icon name="search" size={18} />
          </span>
          <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-1)' }}>No projects match “{query}”</span>
          <span style={{ fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.5, maxWidth: 240 }}>
            Try a project name or number — like “Harbor” or “RMC-2024”.
          </span>
        </div>
      )}
    </div>
  );
};

/* ─────────────── Search field ─────────────── */

const SearchField = React.forwardRef(function SearchField(
  { value, focused, onFocus, onBlur, onChange, onKeyDown, onClear },
  ref
) {
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        height: 36,
        width: '100%',
        padding: '0 8px 0 11px',
        borderRadius: 8,
        background: focused ? 'var(--bg-surface)' : 'var(--bg-surface-2)',
        border: `1px solid ${focused ? 'var(--accent)' : 'var(--border-default)'}`,
        boxShadow: focused ? FOCUS_RING : 'none',
        transition: 'background 120ms, border-color 120ms, box-shadow 120ms',
      }}
    >
      <span style={{ color: focused ? 'var(--accent-text)' : 'var(--text-3)', flex: '0 0 auto', display: 'inline-flex', transition: 'color 120ms' }}>
        <Icon name="search" size={15} />
      </span>
      <input
        ref={ref}
        value={value}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Search projects…"
        aria-label="Search projects"
        style={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          border: 0,
          outline: 'none',
          background: 'transparent',
          font: 'inherit',
          fontSize: 14.5,
          color: 'var(--text-1)',
          letterSpacing: '-0.006em',
        }}
      />
      {value ? (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            onClear();
          }}
          aria-label="Clear search"
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            flex: '0 0 auto',
            color: 'var(--text-3)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="x" size={14} />
        </button>
      ) : (
        <span
          className="mono"
          aria-hidden="true"
          style={{
            flex: '0 0 auto',
            minWidth: 18,
            height: 20,
            padding: '0 6px',
            borderRadius: 5,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--text-3)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            boxShadow: 'var(--shadow-xs)',
          }}
        >
          /
        </span>
      )}
    </div>
  );
});

/* ─────────────── Identity (title + breadcrumb) ─────────────── */

const Identity = ({ title, crumbs }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
    {crumbs && crumbs.length > 0 && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--text-3)', marginBottom: 1 }}>
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            <span style={{ whiteSpace: 'nowrap' }}>{c}</span>
            <span style={{ color: 'var(--text-4)', display: 'inline-flex' }}>
              <Icon name="chevron-r" size={12} sw={1.8} />
            </span>
          </React.Fragment>
        ))}
      </div>
    )}
    <h1 style={{ margin: 0, fontSize: 17.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.015em', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
      {title}
    </h1>
  </div>
);

/* ─────────────── Bell ─────────────── */

const Bell = ({ count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`Notifications${count ? ` (${count} unread)` : ''}`}
    style={{
      position: 'relative',
      width: 36,
      height: 36,
      borderRadius: 8,
      flex: '0 0 auto',
      color: 'var(--text-2)',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-default)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: 'var(--shadow-xs)',
      cursor: 'pointer',
    }}
  >
    <Icon name="bell" size={17} />
    {count > 0 && (
      <span
        className="mono"
        style={{
          position: 'absolute',
          top: -5,
          right: -5,
          minWidth: 17,
          height: 17,
          padding: '0 4px',
          borderRadius: 999,
          background: 'var(--danger)',
          color: '#fff',
          fontSize: 11.5,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid var(--bg-surface)',
          lineHeight: 1,
        }}
      >
        {count > 99 ? '99+' : count}
      </span>
    )}
  </button>
);

/* ─────────────── media query hook ─────────────── */

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

/* ═══════════════════════════════════════════════════════════════════════════
   AppHeader
   ═══════════════════════════════════════════════════════════════════════════ */

export default function AppHeader({ title, crumbs = null, action = null, onOpenProject, onBellClick, notifCount = 0 }) {
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(false);
  const [narrowOpen, setNarrowOpen] = useState(false);

  const narrow = useMediaQuery('(max-width: 860px)');

  const ensureProjects = useCallback(() => {
    if (projects !== null || loading) return;
    setLoading(true);
    getProjects({})
      .then((d) => setProjects(Array.isArray(d) ? d : []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [projects, loading]);

  const results = useMemo(() => {
    const s = query.trim().toLowerCase();
    if (!s || !projects) return [];
    return projects.filter(
      (p) =>
        (p.project_name || '').toLowerCase().includes(s) ||
        (p.project_number || '').toLowerCase().includes(s)
    );
  }, [query, projects]);

  // dropdown state machine
  let state = null;
  if (open) {
    if (loading && projects === null) state = 'loading';
    else if (query.trim() === '') state = 'recent';
    else if (results.length === 0) state = 'nomatch';
    else state = 'results';
  }
  const rows = state === 'recent' ? (projects || []).slice(0, 6) : results;

  useEffect(() => {
    setActive(0);
  }, [query]);

  // "/" focuses the search field (unless typing in a field already)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/') return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
      e.preventDefault();
      if (narrow) setNarrowOpen(true);
      // focus on next tick so the field exists if it was collapsed
      requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [narrow]);

  const pick = (p) => {
    setOpen(false);
    setNarrowOpen(false);
    if (inputRef.current) inputRef.current.blur();
    if (onOpenProject && p) onOpenProject(p.slug || p.id);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(rows.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rows[active]) pick(rows[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setNarrowOpen(false);
      if (inputRef.current) inputRef.current.blur();
    }
  };

  const searchContainer = (
    <div style={{ position: 'relative', width: narrow ? '100%' : 'clamp(280px, 30vw, 360px)', flex: narrow ? '1 1 auto' : '0 1 360px' }}>
      <SearchField
        ref={inputRef}
        value={query}
        focused={open}
        onFocus={() => {
          setOpen(true);
          ensureProjects();
        }}
        onBlur={() => {
          setOpen(false);
          if (narrow) setNarrowOpen(false);
        }}
        onChange={setQuery}
        onKeyDown={onKeyDown}
        onClear={() => {
          setQuery('');
          if (inputRef.current) inputRef.current.focus();
        }}
      />
      {state && <SearchDropdown state={state} query={query} rows={rows} results={results} active={active} onPick={pick} />}
    </div>
  );

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        flex: '0 0 56px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        height: 56,
        padding: '0 20px',
        background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {/* When narrow + search expanded, the field overlays the bar full-width */}
      {narrow && narrowOpen ? (
        searchContainer
      ) : (
        <>
          <Identity title={title} crumbs={crumbs} />
          <div style={{ flex: 1, minWidth: 12 }} />
          {narrow ? (
            <button
              type="button"
              aria-label="Search projects"
              onClick={() => {
                setNarrowOpen(true);
                requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
              }}
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                flex: '0 0 auto',
                color: 'var(--text-2)',
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border-default)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Icon name="search" size={16} />
            </button>
          ) : (
            searchContainer
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
            {action}
            {onBellClick && <Bell count={notifCount} onClick={onBellClick} />}
          </div>
        </>
      )}
    </header>
  );
}
